import { BrowseLevelsToolbarActions } from "#browse-levels-toolbar-actions";
import { ToolbarActionsLane } from "@-/dev.improve.haniverse.components.ui.toolbar.button.icon";
import { ToolbarViewButton } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { useAdminAccess } from "@-/dev.improve.haniverse.lib.admin.access";
import { PrimaryControlsStripAccordion } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.toolbar";
import { BrowseLevelsLayoutContext } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels._context";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useEffect } from "react";
import {
  CustomLevelsToolbarButtons,
  useCustomLevelsContext,
} from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels.custom";

/**
 * The Levels bar: the leading actions, and between them and the app shell's drawer trigger the
 * Simplified / Traditional tabs that switch which script's curriculum is browsed. Characters and
 * words share one curriculum (word levels sit between character levels as `11-W14`, `11-W15`, …),
 * with a Custom tab for authored packs (admins only). Editing replaces these tabs with Save,
 * Cancel and Tools. The top-left More menu stays published on every tab, including Custom.
 */
export function BrowseLevelsToolbar() {
  const custom = useCustomLevelsContext();
  const { isCustom, isEditing, setIsCustom } = custom;
  const isAdmin = useAdminAccess();
  const [browsedHanziScript, setBrowsedHanziScript] = useContextSelectors(
    BrowseLevelsLayoutContext,
    (c) => c.browsedHanziScript,
    (c) => c.setBrowsedHanziScript,
  );
  // Non-admins never see Custom; leave the authored view if access is lost mid-session.
  useEffect(() => {
    if (!isAdmin && isCustom) {
      setIsCustom(false);
    }
  }, [isAdmin, isCustom, setIsCustom]);
  return (
    // Opaque chrome: the curriculum material tabs pin under this bar, so content must not scroll
    // through as glass (iOS 26). Matches Characters grade / Words HSK (`floating={false}`).
    <PrimaryControlsStripAccordion
      actions={
        <ToolbarActionsLane reserveProfileTriggerLane>
          <BrowseLevelsToolbarActions />
        </ToolbarActionsLane>
      }
    >
      {isCustom && isEditing ? (
        <CustomLevelsToolbarButtons />
      ) : (
        <>
          <ToolbarViewButton
            label="Simplified"
            onPress={() => {
              setIsCustom(false);
              setBrowsedHanziScript("simplified");
            }}
            selected={!isCustom && browsedHanziScript === "simplified"}
            testID="browse-levels-simplified-tab"
          />
          {/* The Traditional curriculum is still a "Coming Soon" preview
              (BrowseLevelsComingSoonCurriculumNotice). A tab that only shows that notice reads as
              unfinished to App Review (guideline 2.1), so only admins see it until it ships. */}
          {isAdmin || browsedHanziScript === "traditional" ? (
            <ToolbarViewButton
              label="Traditional"
              onPress={() => {
                setIsCustom(false);
                setBrowsedHanziScript("traditional");
              }}
              selected={!isCustom && browsedHanziScript === "traditional"}
              testID="browse-levels-traditional-tab"
            />
          ) : null}
          {isAdmin ? (
            <ToolbarViewButton
              label="Custom"
              onPress={() => setIsCustom(true)}
              selected={isCustom}
              testID="browse-levels-custom-tab"
            />
          ) : null}
        </>
      )}
    </PrimaryControlsStripAccordion>
  );
}
